import MicRoundedIcon from "@mui/icons-material/MicRounded";
import { Box } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { ReactNode } from "react";
import {
  voiceClusterPhaseColors,
  voiceClusterPhaseFill,
  voiceClusterPhaseHover,
  voiceClusterReducedMotionSx,
  voiceClusterRing,
  voiceSpeakingBar,
  voiceStatusBreathe,
  voiceStatusRing,
  voiceThinkingDot,
} from "../aiAssist/animations";
import { maskIconSx } from "../maskIcon";
import type { VoiceDialogPhase } from "../voiceLaunch";

/** Brand PNG glyphs used across the drawer chrome. */
export const ASSISTANT_ICON_SRC = "/assets/tower-assistant-icon.png";
export const EMPTY_STATE_ICON_SRC = "/assets/ai-assist-icon.png?v=3";
export const EXPAND_ICON_SRC = "/assets/tower-assistant-expand-icon.png?v=2";
export const SEND_ICON_SRC = "/assets/tower-assistant-send-icon.png";
export const VOICE_ICON_SRC = "/assets/tower-assistant-voice-icon.png";

export const HEADER_ACTION_SIZE_PX = 30;
export const HEADER_ACTION_RADIUS_PX = 6;
export const HEADER_ACTION_ICON_SIZE_PX = 15;

export const COMPOSER_ACTION_SIZE_PX = 30;
export const COMPOSER_ACTION_RADIUS_PX = 6;
export const COMPOSER_ACTION_ICON_SIZE_PX = 14;

const VOICE_SPEAKING = "voice-speaking";
const VOICE_THINKING = "voice-thinking";

function glyphMotionClass(motion: "speaking" | "thinking" | undefined): string | undefined {
  if (motion === "speaking") {
    return VOICE_SPEAKING;
  }
  if (motion === "thinking") {
    return VOICE_THINKING;
  }
  return undefined;
}

/** Shared chrome for the header action IconButtons (history, tasks, close, …). */
export function headerActionButtonSx(theme: Theme) {
  const assist = theme.app.aiAssist;
  return {
    boxSizing: "border-box" as const,
    width: HEADER_ACTION_SIZE_PX,
    height: HEADER_ACTION_SIZE_PX,
    minWidth: HEADER_ACTION_SIZE_PX,
    minHeight: HEADER_ACTION_SIZE_PX,
    p: 0,
    borderRadius: `${HEADER_ACTION_RADIUS_PX}px`,
    color: assist.textMuted,
    // Same hover fill as multi-select filter option rows (e.g. States "AK Alaska").
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      color: assist.textStrong,
    },
    "& .MuiSvgIcon-root, & .header-action-icon": {
      fontSize: HEADER_ACTION_ICON_SIZE_PX,
      width: HEADER_ACTION_ICON_SIZE_PX,
      height: HEADER_ACTION_ICON_SIZE_PX,
    },
  };
}

/** Shared 30px frame for the composer voice button and the buttons that slide out of it. */
export function composerVoiceButtonFrame() {
  return {
    boxSizing: "border-box" as const,
    width: COMPOSER_ACTION_SIZE_PX,
    height: COMPOSER_ACTION_SIZE_PX,
    minWidth: COMPOSER_ACTION_SIZE_PX,
    minHeight: COMPOSER_ACTION_SIZE_PX,
    p: 0,
    borderRadius: `${COMPOSER_ACTION_RADIUS_PX}px`,
    border: "none",
  };
}

/** Shared chrome for composer end-adornment IconButtons (voice, send). */
export function composerActionButtonSx(theme: Theme, opts?: { bordered?: boolean }) {
  const bordered = opts?.bordered === true;
  const assist = theme.app.aiAssist;
  return {
    ...composerVoiceButtonFrame(),
    border: bordered ? "1px solid" : "none",
    borderColor: bordered ? assist.textMuted : "transparent",
    color: assist.textMuted,
    backgroundColor: "transparent",
    // Same hover as context chips / sibling composer action (fill + strong tint).
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      borderColor: bordered ? assist.textMuted : "transparent",
      color: assist.textStrong,
    },
  };
}

/** Custom PNG glyph tinted via luminance mask so it follows the button's `color` / hover. */
export function MaskedImageIcon({ src, sizePx }: Readonly<{ src: string; sizePx: number }>) {
  return (
    <Box
      className="header-action-icon"
      component="span"
      aria-hidden
      sx={{
        display: "block",
        boxSizing: "border-box",
        width: sizePx,
        height: sizePx,
        flexShrink: 0,
        backgroundColor: "currentColor",
        ...maskIconSx(src),
      }}
    />
  );
}

/** Waveform for the launcher mic. The composer dialog button uses its own glyphs. */
export function VoiceDialogIcon({ sizePx }: Readonly<{ sizePx: number }>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={sizePx}
      height={sizePx}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      aria-hidden
      style={{ display: "block" }}
    >
      <path d="M3 10v4M7.5 6.5v11M12 3v18M16.5 7.5v9M21 10v4" />
    </svg>
  );
}

function GlyphSvg({
  sizePx,
  motion,
  children,
}: Readonly<{ sizePx: number; motion?: "speaking" | "thinking"; children: ReactNode }>) {
  return (
    <svg
      className={glyphMotionClass(motion)}
      viewBox="0 0 24 24"
      width={sizePx}
      height={sizePx}
      fill="currentColor"
      aria-hidden
      style={{ display: "block" }}
    >
      {children}
    </svg>
  );
}

/** Filled bars and dots for the composer voice-dialog button. Color is currentColor. */
function ComposerVoicePhaseGlyph({
  phase,
  sizePx,
}: Readonly<{ phase: VoiceDialogPhase; sizePx: number }>) {
  if (phase === "listening") {
    return <MicRoundedIcon aria-hidden sx={{ width: sizePx, height: sizePx, fontSize: sizePx }} />;
  }
  if (phase === "speaking") {
    // Equalizer from the speaking asset: short, tall, tallest, tall.
    return (
      <GlyphSvg sizePx={sizePx} motion="speaking">
        <rect x="4" y="9" width="3" height="6" rx="1.5" />
        <rect x="8" y="4.5" width="4" height="15" rx="1.75" />
        <rect x="13" y="3.5" width="3" height="17" rx="1.5" />
        <rect x="17" y="4.5" width="4" height="15" rx="1.75" />
      </GlyphSvg>
    );
  }
  if (phase === "thinking") {
    return (
      <GlyphSvg sizePx={sizePx} motion="thinking">
        <circle cx="6.5" cy="12" r="2.2" />
        <circle cx="11.5" cy="12" r="2.2" />
        <circle cx="16.5" cy="12" r="2.2" />
      </GlyphSvg>
    );
  }
  return (
    <GlyphSvg sizePx={sizePx}>
      <rect x="1.8" y="9" width="2.4" height="6" rx="1.2" />
      <rect x="6.3" y="6" width="2.4" height="12" rx="1.2" />
      <rect x="10.8" y="2" width="2.4" height="20" rx="1.2" />
      <rect x="15.3" y="6" width="2.4" height="12" rx="1.2" />
      <rect x="19.8" y="9" width="2.4" height="6" rx="1.2" />
    </GlyphSvg>
  );
}

/** Composer voice-dialog glyph. Color is currentColor, so the button fill tints it. */
export function ComposerVoiceDialogIcon({
  phase,
  sizePx,
}: Readonly<{ phase: VoiceDialogPhase; sizePx: number }>) {
  return <ComposerVoicePhaseGlyph phase={phase} sizePx={sizePx} />;
}

/**
 * Chrome for the composer's voice-dialog button. A running or paused dialog
 * keeps its phase fill. Idle matches the transparent composer action buttons
 * in light mode and keeps the quiet raised fill in dark mode.
 */
export function composerVoiceDialogButtonSx(theme: Theme, phase: VoiceDialogPhase, paused = false) {
  const assist = theme.app.aiAssist;
  const frame = {
    ...composerVoiceButtonFrame(),
    color: assist.onAccent,
    [`& .${VOICE_SPEAKING} rect, & .${VOICE_THINKING} circle`]: {
      transformBox: "fill-box",
      transformOrigin: "center",
    },
    [`& .${VOICE_SPEAKING} rect`]: {
      animation: `${voiceSpeakingBar} 0.9s ease-in-out infinite`,
    },
    [`& .${VOICE_SPEAKING} rect:nth-of-type(2)`]: { animationDelay: "0.15s" },
    [`& .${VOICE_SPEAKING} rect:nth-of-type(3)`]: { animationDelay: "0.3s" },
    [`& .${VOICE_SPEAKING} rect:nth-of-type(4)`]: { animationDelay: "0.45s" },
    [`& .${VOICE_THINKING} circle`]: {
      animation: `${voiceThinkingDot} 1.2s ease-in-out infinite`,
    },
    [`& .${VOICE_THINKING} circle:nth-of-type(2)`]: { animationDelay: "0.2s" },
    [`& .${VOICE_THINKING} circle:nth-of-type(3)`]: { animationDelay: "0.4s" },
    "@media (prefers-reduced-motion: reduce)": {
      [`& .${VOICE_SPEAKING} rect, & .${VOICE_THINKING} circle`]: { animation: "none" },
    },
  };
  if (phase === "idle") {
    if (theme.palette.mode === "light") {
      return composerActionButtonSx(theme);
    }
    return {
      ...frame,
      backgroundColor: assist.raisedBg,
      backgroundImage: "none",
      color: assist.textFaint,
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
        backgroundImage: "none",
        color: assist.kindBlue,
      },
    };
  }
  const colors = voiceClusterPhaseColors(theme);
  const backgroundColor = voiceClusterPhaseFill(colors, phase);
  const hoverColor = voiceClusterPhaseHover(colors, phase);
  const ringing = !paused && (phase === "listening" || phase === "speaking");
  const breathing = !paused && phase === "thinking";
  const ring = voiceClusterRing(backgroundColor, theme.palette.mode);
  const halo = {
    content: '""',
    position: "absolute" as const,
    inset: 0,
    zIndex: 0,
    borderRadius: `${COMPOSER_ACTION_RADIUS_PX}px`,
    pointerEvents: "none" as const,
    opacity: 0,
  };
  return {
    ...frame,
    position: "relative",
    overflow: "visible",
    backgroundColor,
    backgroundImage: "none",
    "&::before": {
      ...halo,
      ...(ringing ? { bgcolor: ring, animation: `${voiceStatusRing} 1.6s ease-out infinite` } : {}),
      ...(breathing
        ? { bgcolor: backgroundColor, animation: `${voiceStatusBreathe} 2.4s ease-in-out infinite` }
        : {}),
    },
    "&::after": {
      ...halo,
      ...(ringing
        ? {
            bgcolor: ring,
            animation: `${voiceStatusRing} 1.6s ease-out infinite`,
            animationDelay: "0.8s",
          }
        : {}),
    },
    "&:hover": {
      backgroundColor: hoverColor,
      backgroundImage: "none",
      color: assist.onAccent,
    },
    "&.Mui-disabled": {
      backgroundColor,
      backgroundImage: "none",
      color: assist.onAccent,
      opacity: 0.6,
    },
    "&.Mui-disabled:hover": {
      backgroundColor,
    },
    ...voiceClusterReducedMotionSx(),
  };
}

/** Listening fill for the mic that slides out of the composer voice button. */
export function composerVoiceListenButtonSx(theme: Theme) {
  const assist = theme.app.aiAssist;
  const colors = voiceClusterPhaseColors(theme);
  const backgroundColor = colors.listening;
  const hoverColor = voiceClusterPhaseHover(colors, "listening");
  return {
    ...composerVoiceButtonFrame(),
    color: assist.onAccent,
    backgroundColor,
    backgroundImage: "none",
    "&:hover": {
      backgroundColor: hoverColor,
      color: assist.onAccent,
    },
    "&.Mui-disabled": {
      backgroundColor,
      color: assist.onAccent,
      opacity: 0.6,
    },
  };
}

/**
 * Neutral close control, same 30px frame as the composer voice button.
 * Colors match the floating stop button: card fill, hairline, strong glyph.
 */
export function composerVoiceCloseButtonSx(theme: Theme) {
  const assist = theme.app.aiAssist;
  const dark = theme.palette.mode === "dark";
  const borderColor = dark ? assist.avatarBg : assist.border;
  const hoverBg = dark ? assist.avatarBg : assist.raisedBg;
  return {
    ...composerVoiceButtonFrame(),
    border: "1px solid",
    borderColor,
    backgroundColor: assist.cardBg,
    backgroundImage: "none",
    boxShadow: assist.bubbleShadow,
    color: assist.textStrong,
    "&:hover": {
      backgroundColor: hoverBg,
      borderColor,
      boxShadow: assist.bubbleShadow,
      color: assist.textTitle,
    },
  };
}

export function HeaderActionImageIcon({ src }: Readonly<{ src: string }>) {
  return <MaskedImageIcon src={src} sizePx={HEADER_ACTION_ICON_SIZE_PX} />;
}
